import { v4 as uuidv4 } from "uuid";
import { makeAutoObservable, runInAction } from "mobx";
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";

import db from "../FirestoreConfig";
import {
  CLIENT_NUTRITION_DEFAULT_PURPOSE,
  CLIENT_NUTRITION_STATUS_ACTIVE,
  CLIENT_NUTRITION_STATUS_ARCHIVED,
} from "./nutritionPaths";
import SyncDomainController, {
  createBlockedSyncError,
  createFatalSyncError,
} from "../../sync/SyncDomainController";
import NutritionPlan from "./NutritionPlan";
import {
  clonePlainValue,
  NutritionEntitySyncStatus,
} from "./OptimisticNutritionEntity";
import {
  buildClientEditablePlanRootRecord,
  CLIENT_EDITABLE_PLAN_RECORD_KIND,
  buildClientNutritionPlanMealRecord,
} from "./nutritionRecords";
import {
  buildPlanDraftForMerge,
  buildPlanMealsQuery,
  collectDocs,
  createPlanEntityKey,
  createPlanReadKey,
  isPlanReadKey,
  noop,
  normalizePlanId,
  persistResolvedNutritionPlanDraft,
  sortClientEditablePlans,
  sortMealsByOrder,
} from "./nutritionPlanSync";
import {
  buildClientNutritionPlanListItem,
  reconcileClientNutritionPlanListItem,
  sortClientNutritionPlanListItems,
} from "../../nutrition/plans/clientNutritionPlanListUtils";
import { isPlanDraftSemanticallyEmpty } from "../../nutrition/plans/nutritionPlanLogic";
import { resolveClientNutritionWorkspace } from "../../invitations/clientNutritionPathResolver";
import { resolveNutritionDomainStatus } from "./nutritionSyncStatus";

function isSupportedClientPlanRecord(record) {
  return String(record?.record_kind ?? "").trim() === CLIENT_EDITABLE_PLAN_RECORD_KIND;
}

function hasOwn(source, key) {
  return Object.prototype.hasOwnProperty.call(source ?? {}, key);
}

function isPendingScopeKey(scopeKey) {
  return String(scopeKey ?? "").trim().startsWith("pending:");
}

function isActiveClientScopeKey(scopeKey) {
  return String(scopeKey ?? "").trim().startsWith("client:");
}

function shouldStartWorkspacePromotionTransition(previousScopeKey, nextScopeKey) {
  return isPendingScopeKey(previousScopeKey) && isActiveClientScopeKey(nextScopeKey);
}

function normalizeTouchedPaths(paths = []) {
  return Array.from(
    new Set(
      (Array.isArray(paths) ? paths : [])
        .map((path) => String(path ?? "").trim())
        .filter(Boolean),
    ),
  );
}

function buildFallbackTouchedPaths(snapshot = null) {
  return Object.keys(snapshot && typeof snapshot === "object" ? snapshot : {});
}

export class ClientNutritionPlanStore {
  isFetchingPlans = false;
  hasFetchedPlans = false;
  planEntitiesById = new Map();
  planRootRecordsById = new Map();
  planMealsByPlanId = new Map();
  browserPlanItemsById = new Map();
  syncController = null;
  workspaceBinding = null;
  workspaceEpoch = 0;
  workspaceTransition = null;

  constructor(client, options = {}) {
    this.client = client;
    this.syncController =
      options.syncController ?? new SyncDomainController("nutrition-client-plans");
    this.workspaceBinding = resolveClientNutritionWorkspace(client);
    makeAutoObservable(
      this,
      {
        browserPlanItemsById: false,
        client: false,
        workspaceBinding: false,
      },
      { autoBind: true },
    );
  }

  get nutritionWorkspace() {
    return this.workspaceBinding;
  }

  get clientId() {
    return String(this.nutritionWorkspace?.userId ?? "").trim();
  }

  get planScopeKey() {
    return String(this.nutritionWorkspace?.scopeKey ?? "client:unknown");
  }

  buildPlansReadKey(scopeKey = this.planScopeKey) {
    return `nutrition:${String(scopeKey ?? "client:unknown")}:read:plans`;
  }

  get plansReadKey() {
    return this.buildPlansReadKey();
  }

  get plans() {
    return Array.from(this.planEntitiesById.values());
  }

  get sortedPlans() {
    return sortClientEditablePlans(this.plans);
  }

  buildBrowserPlanItem(planId) {
    return buildClientNutritionPlanListItem({
      planId,
      entity: this.planEntitiesById.get(planId) ?? null,
      rootRecord: this.planRootRecordsById.get(planId) ?? null,
    });
  }

  reconcileBrowserPlanItem(planId) {
    const normalizedPlanId = normalizePlanId(planId, "plan");
    const nextPlanItem = this.buildBrowserPlanItem(normalizedPlanId);
    if (!nextPlanItem) {
      this.browserPlanItemsById.delete(normalizedPlanId);
      return null;
    }

    const currentPlanItem = this.browserPlanItemsById.get(normalizedPlanId) ?? null;
    const reconciledPlanItem = reconcileClientNutritionPlanListItem(
      currentPlanItem,
      nextPlanItem,
    );
    this.browserPlanItemsById.set(normalizedPlanId, reconciledPlanItem);
    return reconciledPlanItem;
  }

  get listPlans() {
    const planIds = new Set([
      ...this.planEntitiesById.keys(),
      ...this.planRootRecordsById.keys(),
    ]);

    Array.from(this.browserPlanItemsById.keys()).forEach((planId) => {
      if (!planIds.has(planId)) {
        this.browserPlanItemsById.delete(planId);
      }
    });

    return [...planIds]
      .map((planId) => this.reconcileBrowserPlanItem(planId))
      .filter(Boolean);
  }

  get sortedListPlans() {
    return sortClientNutritionPlanListItems(this.listPlans);
  }

  get hasUnsyncedLocalChanges() {
    return this.plans.some((plan) => plan?.hasUnsyncedChanges);
  }

  get syncStatus() {
    return this.status;
  }

  get status() {
    return resolveNutritionDomainStatus({
      controllerStatus: this.syncController?.status,
      entities: this.plans,
    });
  }

  retryFailed() {
    this.syncController?.retryFailed?.();
  }

  flush(entityKey = null) {
    this.syncController?.flush?.(entityKey);
  }

  getReadableNutritionWorkspace() {
    const nextWorkspace = resolveClientNutritionWorkspace(this.client);
    if (
      String(nextWorkspace?.scopeKey ?? "") !==
      String(this.workspaceBinding?.scopeKey ?? "")
    ) {
      this.rebindClientWorkspace(this.client);
    }

    return this.workspaceBinding;
  }

  async getWritableNutritionWorkspace() {
    this.getReadableNutritionWorkspace();

    const routingResult =
      typeof this.client?.ensureWritableNutritionRouting === "function"
        ? await this.client.ensureWritableNutritionRouting()
        : { ok: true };

    if (!routingResult?.ok) {
      throw createBlockedSyncError(
        "Cannot save client nutrition until invitation routing is reconciled",
        {
          reason: routingResult?.reason ?? "accepted_unmapped_deferred",
        },
      );
    }

    const writableWorkspace = this.getReadableNutritionWorkspace();
    if (!writableWorkspace?.rootDocPathSegments) {
      throw createFatalSyncError(
        "Cannot initialize client nutrition without a storage workspace",
      );
    }

    return writableWorkspace;
  }

  get isWorkspaceTransitionActive() {
    return this.workspaceTransition?.status === "moving";
  }

  getWorkspaceTransitionForPlan(planId = null) {
    const normalizedPlanId = String(planId ?? "").trim().toLowerCase();
    if (!normalizedPlanId) {
      return null;
    }

    return normalizedPlanId === this.workspaceTransition?.editingPlanId
      ? this.workspaceTransition
      : null;
  }

  isPlanInWorkspaceTransition(planId = null) {
    return this.getWorkspaceTransitionForPlan(planId) != null;
  }

  isTransitioningPlan(planId = null) {
    if (this.getWorkspaceTransitionForPlan(planId)?.status !== "moving") {
      return false;
    }

    return true;
  }

  shouldRetainPlanDuringWorkspaceTransition(planId) {
    if (this.workspaceTransition?.status !== "moving") {
      return false;
    }

    const normalizedPlanId = String(planId ?? "").trim().toLowerCase();
    if (!normalizedPlanId) {
      return false;
    }

    return (this.workspaceTransition?.retainedPlanIds ?? []).includes(normalizedPlanId);
  }

  maybeCompleteWorkspaceTransition() {
    const currentTransition = this.workspaceTransition;
    if (currentTransition?.status !== "moving") {
      return false;
    }

    if (this.hasFetchedPlans !== true) {
      return false;
    }

    const editingPlanId = String(currentTransition?.editingPlanId ?? "").trim().toLowerCase();
    if (editingPlanId && !this.planEntitiesById.has(editingPlanId)) {
      return false;
    }

    this.workspaceTransition = null;
    return true;
  }

  markWorkspaceTransitionError(reason = "workspace_transition_failed") {
    if (this.workspaceTransition == null) {
      return false;
    }

    this.workspaceTransition = {
      ...this.workspaceTransition,
      status: "error",
      reason: String(reason ?? "workspace_transition_failed").trim() ||
        "workspace_transition_failed",
    };
    return true;
  }

  buildWorkspaceTransitionState({ previousScopeKey, nextScopeKey } = {}) {
    if (!shouldStartWorkspacePromotionTransition(previousScopeKey, nextScopeKey)) {
      return null;
    }

    const editingPlanId =
      String(this.client?.nutritionEditingPlanId ?? "").trim().toLowerCase() || null;
    const capturesByPlanId = {};

    this.planEntitiesById.forEach((entity, planId) => {
      const normalizedPlanId = normalizePlanId(planId, "plan");
      if (!entity?.hasUnsyncedChanges) {
        return;
      }

      const draftSnapshot =
        typeof entity?.getDraftSnapshot === "function"
          ? entity.getDraftSnapshot()
          : clonePlainValue(entity?.draft ?? null);
      if (!draftSnapshot) {
        return;
      }

      capturesByPlanId[normalizedPlanId] = {
        planId: normalizedPlanId,
        draftSnapshot,
        serverSnapshot:
          entity?.serverBase != null ? clonePlainValue(entity.serverBase) : null,
        rootRecord: clonePlainValue(
          this.planRootRecordsById.get(normalizedPlanId) ?? null,
        ),
        touchedPaths: normalizeTouchedPaths(entity?.touchedPaths),
        baseRev: Number(entity?.baseRev ?? 0) || 0,
        rev: Number(entity?._rev ?? 0) || 0,
        isPersistable: entity?.isPersistable === true,
        wasTransient:
          Number(entity?._rev ?? 0) <= 0 && entity?.serverBase == null,
        syncStatus: entity?.syncStatus ?? NutritionEntitySyncStatus.DIRTY,
      };
    });

    const retainedPlanIds = Object.keys(capturesByPlanId);
    if (!editingPlanId && retainedPlanIds.length === 0) {
      return null;
    }

    return {
      status: "moving",
      fromScopeKey: previousScopeKey,
      toScopeKey: nextScopeKey,
      editingPlanId,
      retainedPlanIds,
      capturesByPlanId,
      startedAtMs: Date.now(),
    };
  }

  hydrateWorkspaceTransitionPlans(transitionState = this.workspaceTransition) {
    const capturesByPlanId = transitionState?.capturesByPlanId ?? {};

    Object.values(capturesByPlanId).forEach((capture) => {
      const normalizedPlanId = normalizePlanId(capture?.planId, "plan");
      if (!normalizedPlanId || this.planEntitiesById.has(normalizedPlanId)) {
        return;
      }

      const baseSnapshot = capture?.wasTransient
        ? {
            id: normalizedPlanId,
          }
        : capture?.serverSnapshot ?? capture?.rootRecord ?? capture?.draftSnapshot ?? { id: normalizedPlanId };
      const nextEntity = this.createDraftPlan({
        ...baseSnapshot,
        id: normalizedPlanId,
        _rev: capture?.wasTransient
          ? 0
          : Number(capture?.rev ?? capture?.baseRev ?? 0) || 0,
      });
      const touchedPaths =
        capture?.touchedPaths?.length > 0
          ? capture.touchedPaths
          : buildFallbackTouchedPaths(capture?.draftSnapshot);

      nextEntity.applyDraft(capture?.draftSnapshot ?? baseSnapshot, {
        touchedPaths,
        scheduleSave: false,
      });

      if (capture?.wasTransient) {
        nextEntity.serverBase = null;
        nextEntity.baseRev = 0;
        nextEntity._rev = 0;
      } else if (capture?.serverSnapshot != null) {
        nextEntity.serverBase = clonePlainValue(capture.serverSnapshot);
        nextEntity.baseRev = Number(capture?.baseRev ?? capture?.rev ?? 0) || 0;
        nextEntity._rev = Number(capture?.rev ?? capture?.baseRev ?? 0) || 0;
      }

      nextEntity.syncStatus =
        capture?.syncStatus === NutritionEntitySyncStatus.OFFLINE_PENDING
          ? NutritionEntitySyncStatus.OFFLINE_PENDING
          : NutritionEntitySyncStatus.DIRTY;

      if (capture?.isPersistable === true && nextEntity.isPersistable === true) {
        nextEntity.scheduleSave();
      }
    });
  }

  rebindClientWorkspace(nextClient = this.client) {
    const previousScopeKey = String(this.workspaceBinding?.scopeKey ?? "").trim();
    const nextWorkspace = resolveClientNutritionWorkspace(nextClient);
    const nextScopeKey = String(nextWorkspace?.scopeKey ?? "").trim();

    if (previousScopeKey === nextScopeKey) {
      return false;
    }

    const nextWorkspaceTransition = this.buildWorkspaceTransitionState({
      previousScopeKey,
      nextScopeKey,
    });
    const previousSyncController = this.syncController;

    if (previousScopeKey) {
      this.stopSync(previousScopeKey);
    }

    previousSyncController?.dispose?.();

    this.workspaceBinding = nextWorkspace;
    this.syncController = new SyncDomainController("nutrition-client-plans");

    runInAction(() => {
      this.workspaceEpoch += 1;
      this.isFetchingPlans = false;
      this.hasFetchedPlans = false;
      this.workspaceTransition = nextWorkspaceTransition;
      this.browserPlanItemsById.clear();
      this.planRootRecordsById.clear();
      this.planMealsByPlanId.clear();
      this.plans.forEach((plan) => plan.dispose?.());
      this.planEntitiesById.clear();
    });

    this.hydrateWorkspaceTransitionPlans(nextWorkspaceTransition);

    return true;
  }

  getPersistedPlanIds() {
    const persistedIds = new Set();

    this.planRootRecordsById.forEach((planRecord, planId) => {
      if (Number(planRecord?._rev ?? 0) > 0) {
        persistedIds.add(normalizePlanId(planId, "plan"));
      }
    });

    this.planEntitiesById.forEach((entity, planId) => {
      if (Number(entity?._rev ?? 0) > 0 || entity?.serverBase != null) {
        persistedIds.add(normalizePlanId(planId, "plan"));
      }
    });

    return persistedIds;
  }

  reconcileInitialPlansSnapshot(plansSnapshot) {
    const serverPlanIds = new Set();

    (Array.isArray(plansSnapshot?.docs) ? plansSnapshot.docs : []).forEach((docSnapshot) => {
      if (!docSnapshot?.id) {
        return;
      }

      const nextRecord = {
        id: docSnapshot.id,
        ...docSnapshot.data(),
      };

      if (!isSupportedClientPlanRecord(nextRecord)) {
        this.removePlanFromStore(docSnapshot.id);
        return;
      }

      const normalizedPlanId = normalizePlanId(docSnapshot.id, "plan");
      serverPlanIds.add(normalizedPlanId);
      this.updatePlanCache(normalizedPlanId, nextRecord, null);
    });

    this.getPersistedPlanIds().forEach((planId) => {
      if (!serverPlanIds.has(planId)) {
        if (this.shouldRetainPlanDuringWorkspaceTransition(planId)) {
          return;
        }

        this.removePlanFromStore(planId);
      }
    });

    this.maybeCompleteWorkspaceTransition();
  }

  buildMealsMapFromSnapshot(mealsSnapshot) {
    return new Map(
      (Array.isArray(mealsSnapshot?.docs) ? mealsSnapshot.docs : [])
        .filter((mealSnapshot) => mealSnapshot?.id)
        .map((mealSnapshot) => {
          const normalizedMealId = String(mealSnapshot.id).trim().toLowerCase();
          return [
            normalizedMealId,
            {
              id: normalizedMealId,
              ...mealSnapshot.data(),
            },
          ];
        }),
    );
  }

  hydratePlanRecord(planRecord, mealRecords = [], options = {}) {
    return NutritionPlan.fromRecord(planRecord, mealRecords, {
      store: this,
      mealsHydrated: options.mealsHydrated ?? Array.isArray(mealRecords),
    });
  }

  upsertPlanEntity(planRecord, mealRecords = null, options = {}) {
    const normalizedId = normalizePlanId(planRecord?.id, "plan");
    const existingEntity = this.planEntitiesById.get(normalizedId);
    const normalizedPlanRecord = {
      ...planRecord,
      id: normalizedId,
      createdByMe: planRecord?.createdByMe !== false,
    };

    if (existingEntity) {
      if (
        existingEntity.tryApplyNoRefreshServerAck(normalizedPlanRecord, {
          mealRecords,
          mealsHydrated: options.mealsHydrated ?? Array.isArray(mealRecords),
          finalizePendingAck: options.source !== "commit",
        })
      ) {
        return existingEntity;
      }
      existingEntity.applyServerRecord(normalizedPlanRecord, mealRecords);
      if (options.source !== "commit") {
        existingEntity.finalizePendingServerAckAfterMerge(normalizedPlanRecord?._rev);
      }
      return existingEntity;
    }

    const nextEntity = this.hydratePlanRecord(
      normalizedPlanRecord,
      mealRecords ?? [],
      {
        mealsHydrated: options.mealsHydrated ?? Array.isArray(mealRecords),
      },
    );
    this.planEntitiesById.set(normalizedId, nextEntity);
    return nextEntity;
  }

  discardRemoteDeletedEntity(entity) {
    const normalizedId = normalizePlanId(entity?.id, "plan");
    const trackedEntity = this.planEntitiesById.get(normalizedId);

    this.planRootRecordsById.delete(normalizedId);
    this.planMealsByPlanId.delete(normalizedId);
    this.browserPlanItemsById.delete(normalizedId);

    if (trackedEntity === entity) {
      entity.dispose?.();
      this.planEntitiesById.delete(normalizedId);
    }
  }

  queueEntitySave(entity) {
    if (!entity?.id || entity?.isPersistable !== true || !entity.hasUnsyncedChanges) {
      return Promise.resolve(false);
    }

    const entityKey = createPlanEntityKey(this.planScopeKey, entity.id);
    const submittedVersion = entity.localVersion;

    return new Promise((resolve) => {
      this.syncController.enqueueMutation({
        entityKey,
        execute: () => this.persistPlanEntity(entity, { submittedVersion }),
        onQueued: ({ offline }) => {
          entity.noteMutationQueued({ offline });
          if (offline) {
            resolve({
              ok: false,
              kind: "offline_pending",
              queued: true,
            });
          }
        },
        onSuccess: (result, context = {}) => {
          resolve({
            ok: true,
            queued: false,
            superseded: context.superseded === true,
            result,
          });
        },
        onBlocked: (error) => {
          entity.noteMutationBlocked(error);
          resolve({
            ok: false,
            queued: false,
            kind: "blocked",
            error,
          });
        },
        onError: (error, context = {}) => {
          entity.noteMutationError(error, {
            kind: context.kind,
            offline: !this.syncController.isOnline,
          });
          resolve({
            ok: false,
            queued: context.kind !== "fatal",
            kind: context.kind ?? "retryable",
            error,
          });
        },
      });
    });
  }

  createDraftPlan(initialData = {}) {
    const createdAt = initialData?.createdAt ?? new Date();
    const draftPlanId = normalizePlanId(initialData?.id, uuidv4());
    const draftPlan = new NutritionPlan(
      {
        id: draftPlanId,
        createdAt,
        ...initialData,
      },
      {
        store: this,
        mealsHydrated: true,
      },
    );
    this.planEntitiesById.set(draftPlan.id, draftPlan);
    this.planRootRecordsById.set(
      draftPlan.id,
      this.buildPlanRootRecord(
        {
          id: draftPlanId,
          createdAt,
          ...initialData,
        },
        this.buildPlanMealDocs({
          ...initialData,
          id: draftPlanId,
        }),
        {
          createdAt,
          updatedAt: initialData?.updatedAt,
          status: initialData?.status,
          purpose: initialData?.purpose,
          originType: initialData?.origin_type,
          originPlanId: initialData?.origin_plan_id,
          originPlanRev: initialData?.origin_plan_rev,
          activatedAt: hasOwn(initialData, "activated_at")
            ? initialData.activated_at
            : undefined,
          deactivatedAt: hasOwn(initialData, "deactivated_at")
            ? initialData.deactivated_at
            : undefined,
          rev: Number(initialData?._rev ?? 0) || 0,
        },
      ),
    );
    return draftPlan;
  }

  async finalizePlanEditorClose(planId) {
    const normalizedId = normalizePlanId(planId, "plan");
    const entity = this.planEntitiesById.get(normalizedId);

    if (!entity) {
      return { action: "missing" };
    }

    const rev = Number(entity?._rev ?? 0) || 0;
    const hasServerBase = entity.serverBase != null;
    const hasUnsyncedChanges = entity.hasUnsyncedChanges === true;
    const isPersistable = entity.isPersistable === true;
    const isTransientDraft = rev <= 0 && !hasServerBase;
    const isInvalidPersistedDraft =
      rev > 0 && hasServerBase && hasUnsyncedChanges && !isPersistable;
    const isEmptyPersistedDraft =
      isInvalidPersistedDraft &&
      isPlanDraftSemanticallyEmpty(entity.getDraftSnapshot?.() ?? entity.draft);

    let action = "keep";

    if (isTransientDraft) {
      action = "remove_transient";
      this.removeTransientEntity("plan", normalizedId);
    } else if (isEmptyPersistedDraft) {
      action = "delete_empty_persisted";
    } else if (isInvalidPersistedDraft) {
      action = "revert_invalid_persisted";
      this.revertPersistedPlanInvalidDraftOnClose(normalizedId);
    }

    if (action !== "delete_empty_persisted") {
      return { action };
    }

    await this.deletePlan(normalizedId);
    const trackedEntity = this.planEntitiesById.get(normalizedId);
    if (trackedEntity?.deletedRemotely) {
      trackedEntity.discardLocalChanges();
    }

    return { action };
  }

  revertPersistedPlanInvalidDraftOnClose(planId) {
    const normalizedId = normalizePlanId(planId, "plan");
    const entity = this.planEntitiesById.get(normalizedId);

    if (
      !entity ||
      Number(entity?._rev ?? 0) <= 0 ||
      entity.serverBase == null ||
      entity.hasUnsyncedChanges !== true ||
      entity.isPersistable === true
    ) {
      return false;
    }

    entity.discardLocalChanges();
    return true;
  }

  removeTransientEntity(entityType, entityId) {
    if (entityType !== "plan") {
      return;
    }

    const normalizedId = normalizePlanId(entityId, entityType);
    const entity = this.planEntitiesById.get(normalizedId);

    if (!entity) {
      return;
    }

    if (Number(entity?._rev ?? 0) > 0 || entity.serverBase != null) {
      return;
    }

    entity.dispose?.();
    this.browserPlanItemsById.delete(normalizedId);
    this.planEntitiesById.delete(normalizedId);
    this.planRootRecordsById.delete(normalizedId);
    this.planMealsByPlanId.delete(normalizedId);
  }

  buildPlanRootRecord(planData, mealRecords = [], options = {}) {
    return buildClientEditablePlanRootRecord(planData, mealRecords, options);
  }

  buildPlanMealRecord(mealData) {
    return buildClientNutritionPlanMealRecord(mealData, {
      allowEmptyOptions: true,
    });
  }

  buildPlanMealDocs(savedPlan) {
    return (Array.isArray(savedPlan?.meals) ? savedPlan.meals : []).map((meal) =>
      this.buildPlanMealRecord(meal),
    );
  }

  getPlanMealsMap(planId) {
    return this.planMealsByPlanId.get(normalizePlanId(planId, "plan")) ?? null;
  }

  async loadPlanMealRecords(planId, options = {}) {
    const nutritionWorkspace = this.getReadableNutritionWorkspace();
    if (!nutritionWorkspace?.rootDocPathSegments || !planId) {
      return [];
    }

    const normalizedPlanId = normalizePlanId(planId, "plan");
    if (options?.force !== true && this.planMealsByPlanId.has(normalizedPlanId)) {
      return sortMealsByOrder(
        Array.from(this.planMealsByPlanId.get(normalizedPlanId)?.values() ?? []),
      );
    }

    const mealsSnapshot = await getDocs(
      buildPlanMealsQuery(
        collection(
          db,
          ...nutritionWorkspace.buildPlanMealsCollectionPath(normalizedPlanId),
        ),
      ),
    );

    return sortMealsByOrder(
      mealsSnapshot.docs.map((mealDoc) => ({
        id: mealDoc.id,
        ...mealDoc.data(),
      })),
    );
  }

  updatePlanCache(planId, planRecord = null, mealRecords = null, options = {}) {
    const normalizedPlanId = normalizePlanId(planId, "plan");
    const cachedMealRecords = this.planMealsByPlanId.has(normalizedPlanId)
      ? sortMealsByOrder(
          Array.from(this.planMealsByPlanId.get(normalizedPlanId)?.values() ?? []),
        )
      : null;
    const resolvedMealRecords = mealRecords ?? cachedMealRecords;
    const mealsHydrated = resolvedMealRecords !== null;

    if (planRecord && isSupportedClientPlanRecord(planRecord)) {
      const normalizedPlanRecord = {
        id: normalizedPlanId,
        ...planRecord,
      };
      this.planRootRecordsById.set(normalizedPlanId, normalizedPlanRecord);
      this.upsertPlanEntity(normalizedPlanRecord, resolvedMealRecords, {
        mealsHydrated,
        source: options.source,
      });
    } else if (planRecord == null) {
      this.planRootRecordsById.delete(normalizedPlanId);
    }

    if (mealRecords !== null) {
      this.planMealsByPlanId.set(
        normalizedPlanId,
        new Map(
          sortMealsByOrder(mealRecords).map((mealRecord) => [
            String(mealRecord?.id ?? "").trim().toLowerCase(),
            mealRecord,
          ]),
        ),
      );
    }

    if (planRecord && isSupportedClientPlanRecord(planRecord)) {
      runInAction(() => {
        this.hasFetchedPlans = true;
      });
      this.maybeCompleteWorkspaceTransition();
      return this.planEntitiesById.get(normalizedPlanId) ?? null;
    }

    this.maybeCompleteWorkspaceTransition();
    return null;
  }

  removePlanFromStore(planId) {
    const normalizedPlanId = normalizePlanId(planId, "plan");
    if (this.shouldRetainPlanDuringWorkspaceTransition(normalizedPlanId)) {
      return;
    }

    const entity = this.planEntitiesById.get(normalizedPlanId);

    if (entity?.hasUnsyncedChanges) {
      entity.markDeletedRemotely?.();
    } else {
      entity?.dispose?.();
      this.planEntitiesById.delete(normalizedPlanId);
    }

    this.browserPlanItemsById.delete(normalizedPlanId);
    this.planRootRecordsById.delete(normalizedPlanId);
    this.planMealsByPlanId.delete(normalizedPlanId);
  }

  async ensureRootDocument() {
    const nutritionWorkspace = await this.getWritableNutritionWorkspace();
    const updatedAt = serverTimestamp();

    await setDoc(
      doc(db, ...nutritionWorkspace.rootDocPathSegments),
      {
        schema_version: "nutrition_client_v2",
        storage_scope: "client_editable",
        updatedAt,
      },
      { merge: true },
    );

    if (nutritionWorkspace.scope === "pending" && nutritionWorkspace.workspaceDocPathSegments) {
      await setDoc(
        doc(db, ...nutritionWorkspace.workspaceDocPathSegments),
        nutritionWorkspace.buildWorkspaceMetadataPatch({
          lastUpdate: updatedAt,
        }),
        { merge: true },
      );
    }
  }

  async fetchPlansFromCloud({ force = false } = {}) {
    const nutritionWorkspace = this.getReadableNutritionWorkspace();
    if (!nutritionWorkspace?.plansCollectionPathSegments) {
      return [];
    }

    if (force) {
      this.syncController.unregisterRead(this.plansReadKey);
    } else if (this.syncController.reads.has(this.plansReadKey)) {
      return this.sortedPlans;
    }

    runInAction(() => {
      this.isFetchingPlans = true;
    });

    await this.syncController.registerRead(this.plansReadKey, this.startPlansRead);
    return this.sortedPlans;
  }

  startPlansRead({ onReady, onError }) {
    const nutritionWorkspace = this.getReadableNutritionWorkspace();
    if (!nutritionWorkspace?.plansCollectionPathSegments) {
      onReady();
      return noop;
    }

    const expectedWorkspaceEpoch = this.workspaceEpoch;
    let isInitialSnapshot = true;

    return onSnapshot(
      collection(db, ...nutritionWorkspace.plansCollectionPathSegments),
      (plansSnapshot) => {
        if (expectedWorkspaceEpoch !== this.workspaceEpoch) {
          return;
        }

        runInAction(() => {
          if (isInitialSnapshot) {
            this.reconcileInitialPlansSnapshot(plansSnapshot);
            isInitialSnapshot = false;
            this.hasFetchedPlans = true;
            this.isFetchingPlans = false;
            this.maybeCompleteWorkspaceTransition();
            return;
          }

          collectDocs(plansSnapshot).forEach((change) => {
            const docSnapshot = change?.doc;
            if (!docSnapshot?.id) {
              return;
            }

            if (change.type === "removed") {
              this.removePlanFromStore(docSnapshot.id);
              return;
            }

            const nextRecord = {
              id: docSnapshot.id,
              ...docSnapshot.data(),
            };

            if (!isSupportedClientPlanRecord(nextRecord)) {
              this.removePlanFromStore(docSnapshot.id);
              return;
            }

            this.updatePlanCache(docSnapshot.id, nextRecord, null);
          });
          this.hasFetchedPlans = true;
          this.isFetchingPlans = false;
          this.maybeCompleteWorkspaceTransition();
        });
        onReady();
      },
      (error) => {
        if (expectedWorkspaceEpoch !== this.workspaceEpoch) {
          return;
        }

        runInAction(() => {
          this.isFetchingPlans = false;
          this.markWorkspaceTransitionError("plans_read_failed");
        });
        onError(error);
      },
    );
  }

  async watchPlan(planId, onPlanChange = null) {
    const nutritionWorkspace = this.getReadableNutritionWorkspace();
    if (!nutritionWorkspace?.rootDocPathSegments || !planId) {
      return () => {};
    }

    const normalizedPlanId = normalizePlanId(planId, "plan");
    const rootReadKey = createPlanReadKey(this.planScopeKey, normalizedPlanId, "root");
    const mealsReadKey = createPlanReadKey(this.planScopeKey, normalizedPlanId, "meals");
    const expectedWorkspaceEpoch = this.workspaceEpoch;
    let isInitialMealsSnapshot = true;
    const emitPlan =
      typeof onPlanChange === "function"
        ? () => {
            onPlanChange(this.planEntitiesById.get(normalizedPlanId) ?? null);
          }
        : noop;

    await Promise.all([
      this.syncController.registerRead(rootReadKey, ({ onReady, onError }) =>
        onSnapshot(
          doc(db, ...nutritionWorkspace.buildPlanDocPath(normalizedPlanId)),
          (planSnapshot) => {
            if (expectedWorkspaceEpoch !== this.workspaceEpoch) {
              return;
            }

            runInAction(() => {
              if (planSnapshot.exists()) {
                const nextRecord = {
                  id: planSnapshot.id,
                  ...planSnapshot.data(),
                };
                if (isSupportedClientPlanRecord(nextRecord)) {
                  this.updatePlanCache(normalizedPlanId, nextRecord);
                } else {
                  this.removePlanFromStore(normalizedPlanId);
                }
              } else {
                this.removePlanFromStore(normalizedPlanId);
              }
            });
            emitPlan();
            onReady();
          },
          onError,
        ),
      ),
      this.syncController.registerRead(mealsReadKey, ({ onReady, onError }) =>
        onSnapshot(
          buildPlanMealsQuery(
            collection(
              db,
              ...nutritionWorkspace.buildPlanMealsCollectionPath(normalizedPlanId),
            ),
          ),
          (mealsSnapshot) => {
            if (expectedWorkspaceEpoch !== this.workspaceEpoch) {
              return;
            }

            runInAction(() => {
              const nextMealsById = isInitialMealsSnapshot
                ? this.buildMealsMapFromSnapshot(mealsSnapshot)
                : new Map(this.planMealsByPlanId.get(normalizedPlanId) ?? []);

              if (!isInitialMealsSnapshot) {
                collectDocs(mealsSnapshot).forEach((change) => {
                  const mealSnapshot = change?.doc;
                  if (!mealSnapshot?.id) {
                    return;
                  }

                  const normalizedMealId = String(mealSnapshot.id).trim().toLowerCase();

                  if (change.type === "removed") {
                    nextMealsById.delete(normalizedMealId);
                    return;
                  }

                  nextMealsById.set(normalizedMealId, {
                    id: normalizedMealId,
                    ...mealSnapshot.data(),
                  });
                });
              }

              this.planMealsByPlanId.set(normalizedPlanId, nextMealsById);
              const rootRecord = this.planRootRecordsById.get(normalizedPlanId);
              if (rootRecord) {
                this.upsertPlanEntity(
                  rootRecord,
                  sortMealsByOrder(Array.from(nextMealsById.values())),
                  { mealsHydrated: true },
                );
              }

              isInitialMealsSnapshot = false;
            });
            emitPlan();
            onReady();
          },
          onError,
        ),
      ),
    ]);

    emitPlan();

    return () => {
      this.syncController.unregisterRead(rootReadKey);
      this.syncController.unregisterRead(mealsReadKey);
      // Keep `planRootRecordsById` for this plan: it is shared with the client plans
      // collection listener and `buildClientNutritionPlanListItem`. Deleting here made
      // archived plans appear active (entity present, root missing → status defaults).
      this.planMealsByPlanId.delete(normalizedPlanId);
    };
  }

  async persistPlanEntity(entity, options = {}) {
    const resolvedPlanId = normalizePlanId(entity?.id, "plan");
    const draftAtPersistStart = entity.getDraftSnapshot();
    const result = await this.persistResolvedPlanDraft(
      resolvedPlanId,
      () => entity.getDraftSnapshot(),
      {
        baseRev: entity.baseRev,
        touchedPaths: entity.touchedPaths,
        submittedVersion: options.submittedVersion,
        entity,
      },
    );

    return {
      _rev: Number(result?._rev ?? entity?._rev ?? 0) || 0,
      submittedDraft: result?.submittedDraft ?? draftAtPersistStart,
    };
  }

  async persistResolvedPlanDraft(
    planId,
    buildLocalDraft,
    {
      baseRev,
      touchedPaths,
      requireExistingPlan = false,
      rootRecordPatch = null,
      submittedVersion,
      entity = null,
    } = {},
  ) {
    if (!planId) {
      throw createFatalSyncError("Cannot save client nutrition plan without a plan id");
    }

    const expectedWorkspaceEpoch = this.workspaceEpoch;
    const nutritionWorkspace = await this.getWritableNutritionWorkspace();
    const expectedScopeKey = String(nutritionWorkspace?.scopeKey ?? "").trim();
    const normalizedPlanId = normalizePlanId(planId, "plan");
    const cachedRootRecord = this.planRootRecordsById.get(normalizedPlanId) ?? null;
    const result = await persistResolvedNutritionPlanDraft({
      db,
      planId: normalizedPlanId,
      planDocRef: doc(db, ...nutritionWorkspace.buildPlanDocPath(normalizedPlanId)),
      mealsQuery: buildPlanMealsQuery(
        collection(
          db,
          ...nutritionWorkspace.buildPlanMealsCollectionPath(normalizedPlanId),
        ),
      ),
      buildMealDocRef: (resolvedPlanId, mealId) =>
        doc(db, ...nutritionWorkspace.buildPlanMealDocPath(resolvedPlanId, mealId)),
      ensureRootDocument: this.ensureRootDocument,
      buildLocalDraft,
      baseRev,
      touchedPaths,
      requireExistingPlan,
      buildMealDocs: (savedPlan) => this.buildPlanMealDocs(savedPlan),
      buildRootRecord: ({
        savedPlan,
        existingPlanRecord,
        nextMealDocs,
        nextRev,
      }) => {
        const resolvedStatus =
          rootRecordPatch?.status ??
          existingPlanRecord?.status ??
          cachedRootRecord?.status ??
          CLIENT_NUTRITION_STATUS_ACTIVE;
        const resolvedActivatedAt =
          rootRecordPatch && hasOwn(rootRecordPatch, "activated_at")
            ? rootRecordPatch.activated_at
            : existingPlanRecord?.activated_at ??
              cachedRootRecord?.activated_at;
        let resolvedDeactivatedAt;
        if (rootRecordPatch && hasOwn(rootRecordPatch, "deactivated_at")) {
          resolvedDeactivatedAt = rootRecordPatch.deactivated_at;
        } else if (existingPlanRecord && hasOwn(existingPlanRecord, "deactivated_at")) {
          resolvedDeactivatedAt = existingPlanRecord.deactivated_at;
        } else if (cachedRootRecord && hasOwn(cachedRootRecord, "deactivated_at")) {
          resolvedDeactivatedAt = cachedRootRecord.deactivated_at;
        } else if (
          resolvedStatus === CLIENT_NUTRITION_STATUS_ARCHIVED &&
          !isSupportedClientPlanRecord(existingPlanRecord)
        ) {
          resolvedDeactivatedAt = serverTimestamp();
        }

        return this.buildPlanRootRecord(
          {
            ...existingPlanRecord,
            ...savedPlan,
            id: normalizedPlanId,
          },
          nextMealDocs,
          {
            createdAt:
              existingPlanRecord?.createdAt ??
              cachedRootRecord?.createdAt ??
              serverTimestamp(),
            updatedAt: serverTimestamp(),
            status: resolvedStatus,
            purpose:
              rootRecordPatch?.purpose ??
              existingPlanRecord?.purpose ??
              cachedRootRecord?.purpose ??
              CLIENT_NUTRITION_DEFAULT_PURPOSE,
            activatedAt: resolvedActivatedAt,
            deactivatedAt: resolvedDeactivatedAt,
            originType:
              rootRecordPatch?.origin_type ??
              existingPlanRecord?.origin_type ??
              cachedRootRecord?.origin_type ??
              "client_direct",
            originPlanId:
              rootRecordPatch?.origin_plan_id ??
              existingPlanRecord?.origin_plan_id ??
              cachedRootRecord?.origin_plan_id ??
              "",
            originPlanRev:
              rootRecordPatch?.origin_plan_rev ??
              existingPlanRecord?.origin_plan_rev ??
              cachedRootRecord?.origin_plan_rev ??
              null,
            rev: nextRev,
          },
        );
      },
    });

    if (!result?.planDoc) {
      return null;
    }

    if (
      expectedWorkspaceEpoch !== this.workspaceEpoch ||
      expectedScopeKey !== String(this.planScopeKey ?? "").trim()
    ) {
      return {
        entity: null,
        planDoc: result.planDoc,
        mealDocs: result.mealDocs,
        submittedDraft: result.submittedDraft,
        _rev: result._rev,
      };
    }

    runInAction(() => {
      entity?.noteMutationSuccess?.({
        submittedSnapshot: result.submittedDraft,
        submittedVersion,
        nextRev: result._rev,
      });
    });

    const persistedEntity = this.updatePlanCache(
      normalizedPlanId,
      result.planDoc,
      result.mealDocs,
      { source: "commit" },
    );
    return {
      entity: persistedEntity,
      planDoc: result.planDoc,
      mealDocs: result.mealDocs,
      submittedDraft: result.submittedDraft,
      _rev: result._rev,
    };
  }

  async persistPlan(planData, { baseRev } = {}) {
    const resolvedPlanId = normalizePlanId(planData?.id, "plan");
    const nextDraft = buildPlanDraftForMerge(planData, planData?.meals ?? []);
    const result = await this.persistResolvedPlanDraft(
      resolvedPlanId,
      () => nextDraft,
      { baseRev },
    );
    return result?.entity ?? null;
  }

  savePlan(planData, options = {}) {
    return this.syncController.runImmediateMutation({
      entityKey: createPlanEntityKey(this.planScopeKey, planData?.id),
      execute: () => this.persistPlan(planData, options),
    });
  }

  async persistPlanRootPatch(planId, planPatch, { baseRev } = {}) {
    const normalizedPlanId = normalizePlanId(planId, "plan");
    const result = await this.persistResolvedPlanDraft(
      normalizedPlanId,
      (serverDraft) => ({
        ...serverDraft,
        ...planPatch,
      }),
      {
        baseRev,
        requireExistingPlan: true,
      },
    );
    return result?.entity ?? null;
  }

  patchPlanRoot(planId, planPatch, options = {}) {
    return this.syncController.runImmediateMutation({
      entityKey: createPlanEntityKey(this.planScopeKey, planId),
      execute: () => this.persistPlanRootPatch(planId, planPatch, options),
    });
  }

  async persistPlanMeal(planId, mealData, { baseRev } = {}) {
    if (!planId || !mealData?.id) {
      throw createFatalSyncError("Cannot patch client nutrition plan meal without meal data");
    }

    const normalizedPlanId = normalizePlanId(planId, "plan");
    const normalizedMealId = normalizePlanId(mealData.id, "meal");
    const result = await this.persistResolvedPlanDraft(
      normalizedPlanId,
      (serverDraft) => {
        const currentMeals = Array.isArray(serverDraft?.meals) ? serverDraft.meals : [];
        const currentMeal = currentMeals.find((meal) => meal.id === normalizedMealId) ?? null;
        const nextMeal = {
          ...(currentMeal ?? {}),
          ...mealData,
          id: normalizedMealId,
          order: mealData?.order ?? currentMeal?.order ?? currentMeals.length,
        };

        return {
          ...serverDraft,
          meals: sortMealsByOrder(
            currentMeals.filter((meal) => meal.id !== normalizedMealId).concat(nextMeal),
          ),
        };
      },
      {
        baseRev,
        requireExistingPlan: true,
        touchedPaths: ["meals"],
      },
    );
    return result?.entity ?? null;
  }

  patchPlanMeal(planId, mealData, options = {}) {
    return this.syncController.runImmediateMutation({
      entityKey: createPlanEntityKey(this.planScopeKey, planId),
      execute: () => this.persistPlanMeal(planId, mealData, options),
    });
  }

  async addPlanMeal(planId, mealData, options = {}) {
    const normalizedMealId = normalizePlanId(mealData?.id, "meal");
    return this.syncController.runImmediateMutation({
      entityKey: createPlanEntityKey(this.planScopeKey, planId),
      execute: () =>
        this.persistPlanMeal(
          planId,
          {
            ...mealData,
            id: normalizedMealId,
          },
          options,
        ),
    });
  }

  async persistPlanMealDeletion(planId, mealId, { baseRev } = {}) {
    if (!planId || !mealId) {
      throw createFatalSyncError("Cannot delete client nutrition plan meal without ids");
    }

    const normalizedPlanId = normalizePlanId(planId, "plan");
    const normalizedMealId = normalizePlanId(mealId, "meal");
    const result = await this.persistResolvedPlanDraft(
      normalizedPlanId,
      (serverDraft) => {
        const currentMeals = Array.isArray(serverDraft?.meals) ? serverDraft.meals : [];
        const existingMeal = currentMeals.find((meal) => meal.id === normalizedMealId);
        if (!existingMeal) {
          return null;
        }

        return {
          ...serverDraft,
          meals: currentMeals.filter((meal) => meal.id !== normalizedMealId),
        };
      },
      {
        baseRev,
        requireExistingPlan: true,
        touchedPaths: ["meals"],
      },
    );
    return result?.entity ?? null;
  }

  deletePlanMeal(planId, mealId, options = {}) {
    return this.syncController.runImmediateMutation({
      entityKey: createPlanEntityKey(this.planScopeKey, planId),
      execute: () => this.persistPlanMealDeletion(planId, mealId, options),
    });
  }

  async persistPlanMealReorder(planId, nextMeals, { baseRev } = {}) {
    if (!planId) {
      throw createFatalSyncError("Cannot reorder client nutrition plan meals without a plan id");
    }

    const normalizedPlanId = normalizePlanId(planId, "plan");
    const normalizedMeals = sortMealsByOrder(
      (Array.isArray(nextMeals) ? nextMeals : []).map((meal, index) => ({
        ...meal,
        id: normalizePlanId(meal?.id, `meal-${index}`),
        order: index,
      })),
    );
    const result = await this.persistResolvedPlanDraft(
      normalizedPlanId,
      (serverDraft) => ({
        ...serverDraft,
        meals: normalizedMeals,
      }),
      {
        baseRev,
        requireExistingPlan: true,
        touchedPaths: ["meals"],
      },
    );
    return result?.entity ?? null;
  }

  reorderPlanMeals(planId, nextMeals, options = {}) {
    return this.syncController.runImmediateMutation({
      entityKey: createPlanEntityKey(this.planScopeKey, planId),
      execute: () => this.persistPlanMealReorder(planId, nextMeals, options),
    });
  }

  async persistPlanStatus(planId, status, { baseRev } = {}) {
    if (!planId) {
      throw createFatalSyncError("Cannot update client nutrition plan status without a plan id");
    }

    const normalizedPlanId = normalizePlanId(planId, "plan");
    const nextStatus =
      String(status ?? "").trim() === CLIENT_NUTRITION_STATUS_ARCHIVED
        ? CLIENT_NUTRITION_STATUS_ARCHIVED
        : CLIENT_NUTRITION_STATUS_ACTIVE;
    const rootRecordPatch =
      nextStatus === CLIENT_NUTRITION_STATUS_ARCHIVED
        ? {
            status: CLIENT_NUTRITION_STATUS_ARCHIVED,
            deactivated_at: serverTimestamp(),
          }
        : {
            status: CLIENT_NUTRITION_STATUS_ACTIVE,
            activated_at: serverTimestamp(),
            deactivated_at: null,
          };
    const result = await this.persistResolvedPlanDraft(
      normalizedPlanId,
      (serverDraft) => serverDraft,
      {
        baseRev,
        requireExistingPlan: true,
        rootRecordPatch,
      },
    );
    return result?.entity ?? null;
  }

  archivePlan(planId, options = {}) {
    return this.syncController.runImmediateMutation({
      entityKey: createPlanEntityKey(this.planScopeKey, planId),
      execute: () => this.persistPlanStatus(planId, CLIENT_NUTRITION_STATUS_ARCHIVED, options),
    });
  }

  restorePlan(planId, options = {}) {
    return this.syncController.runImmediateMutation({
      entityKey: createPlanEntityKey(this.planScopeKey, planId),
      execute: () => this.persistPlanStatus(planId, CLIENT_NUTRITION_STATUS_ACTIVE, options),
    });
  }

  async persistPlanDeletion(planId) {
    if (!planId) {
      return;
    }

    const nutritionWorkspace = await this.getWritableNutritionWorkspace();
    const normalizedPlanId = normalizePlanId(planId, "plan");
    const activePlanMeals = await this.loadPlanMealRecords(normalizedPlanId);
    const batch = writeBatch(db);

    activePlanMeals.forEach((mealDoc) => {
      batch.delete(
        doc(db, ...nutritionWorkspace.buildPlanMealDocPath(normalizedPlanId, mealDoc.id)),
      );
    });

    batch.delete(doc(db, ...nutritionWorkspace.buildPlanDocPath(normalizedPlanId)));
    await batch.commit();

    this.syncController.unregisterRead(
      createPlanReadKey(this.planScopeKey, normalizedPlanId, "root"),
    );
    this.syncController.unregisterRead(
      createPlanReadKey(this.planScopeKey, normalizedPlanId, "meals"),
    );
    this.removePlanFromStore(normalizedPlanId);
  }

  deletePlan(planId) {
    return this.syncController.runImmediateMutation({
      entityKey: createPlanEntityKey(this.planScopeKey, planId),
      execute: () => this.persistPlanDeletion(planId),
    });
  }

  stopSync(scopeKey = this.planScopeKey) {
    this.syncController.unregisterRead(this.buildPlansReadKey(scopeKey));

    Array.from(this.syncController.reads.keys())
      .filter((readKey) => isPlanReadKey(scopeKey, readKey))
      .forEach((readKey) => this.syncController.unregisterRead(readKey));

    this.browserPlanItemsById.clear();
    this.planRootRecordsById.clear();
    this.planMealsByPlanId.clear();
  }

  reset() {
    this.stopSync();
    this.plans.forEach((plan) => plan.dispose?.());
    this.syncController.dispose();
    this.syncController = new SyncDomainController("nutrition-client-plans");
    this.workspaceBinding = resolveClientNutritionWorkspace(this.client);

    runInAction(() => {
      this.workspaceEpoch += 1;
      this.workspaceTransition = null;
      this.browserPlanItemsById.clear();
      this.planEntitiesById.clear();
      this.isFetchingPlans = false;
      this.hasFetchedPlans = false;
    });
  }
}

export default ClientNutritionPlanStore;
